import { isAccidentalClose, type ModalCloseReason } from '@srm/ui/types/ModalCloseReason';
import React, {
    ReactNode,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
    useSyncExternalStore,
} from 'react';
import clsx from 'clsx';
import isEqual from 'react-fast-compare';
import { IDocumentTable, IRow, IRows, IForms } from '@models/Forms/IForms';
import './TableForm.scss';
import TableFormNavigation from './TableFormNavigation/TableFormNavigation';
import Modal from '@srm/ui/modal';
import Drawer from '@srm/ui/drawer';
import Button from '@srm/ui/button';
import Chip from '@srm/ui/chip';
import Tooltip from '@srm/ui/tooltip';
import Menu from '@srm/ui/menu';
import { MdAdd, MdKeyboardArrowDown, MdOutlineDelete, MdUndo } from 'react-icons/md';
import NestedFormBuilder from '@molecules/formbuilder/NestedFormBuilder/NestedFormBuilder';
import { ITableForm } from '@models/Forms/ITableForm';
import { Controller, useForm } from 'react-hook-form';
import { IField, IFieldElem } from '@models/IFormData';
import { ValueType } from '@srm/ui/types/ValueType';
import { convertListToMap, getFieldKeysByScheme, getFormDataByScheme } from '@utils/documentUtils';
import { ValidatorManager } from '@utils/managers/ValidatorManager';
import { DocumentService } from '@services/DocumentService';
import { sendModalMsg } from '@srm/ui/lib/modalMessages';
import Pagination from '../Pagination';
import {
    TABLE_ADD_MENU_CLASS,
    tableAddChoiceIcon,
    tableAddMenuItems,
} from '@molecules/formbuilder/controls/Table/controls/tableAddMenu';
import { v4 as uuidv4 } from 'uuid';
import FormProviderControl from '@controls/FormProviderControl';
import HookFormProvider from '@srm/ui/controls/HookFormProvider';
import { TableFormProvider } from '@providers/TableFormProvider';
import { useTypedSelector } from '@hooks/useTypedSelector';
import { useLatest } from '@srm/ui/hooks/useLatest';
import MessageDialog from '@srm/ui/message-dialog';
import { useTableFormDrawerWidth } from './useTableFormDrawerWidth';
import { applyTableDisplayMode } from './applyTableDisplayMode';
import { generateTableFormScheme } from './generateTableFormScheme';
import { errorText, errorTextWithTraceId, errorTraceId } from '@srm/core/errors';
import TableFormSubtables from './TableFormSubtables/TableFormSubtables';
import { mergeRowFormValue } from '@utils/mergeRowFormValue';
import { connectRowFormDraft } from './rowFormDraft';
import { createRowFormOperations, RowFormOperationsContext } from './RowFormOperationsContext';
import { createTableFormBridge } from '@molecules/formbuilder/controls/Table/controls/tableFormBridge';
import { collectTableColumns } from '@molecules/formbuilder/controls/Table/controls/tableColumns';
import { validateDocumentTable } from '@utils/validation/document/tableValidation';
import { useDebugPanel } from '@/context/DebugPanelContext';
import { registerFormOperationTracker } from '@utils/formOperationTracker';
import { calculateRowFormTree } from './calculateRowFormTree';
import { useChangeHandlersOptional, useChangeRuntime } from '@controls/ChangeHandlers/useChangeHandlers';
import { isDocumentFieldKey, type IChangeFieldBinding } from '@utils/managers/ChangeRuntime';
import { liveVariablesField } from '@molecules/formbuilder/utils/liveVariablesField';

/** Схемы кастомных форм строк на сессию: форма строки открывается многократно
 *  (навигация по строкам, повторные открытия панели), а схема неизменна —
 *  без кэша каждый показ уходил в сеть и мигал лоадером FormBuilder. */
const customFormSchemeCache = new Map<string, IForms>();

const EMPTY_FIELDS: Record<string, IFieldElem> = {};

/**
 * Открытые формы строк в порядке открытия. Форма строки подтаблицы открывается
 * поверх формы родительской строки, а слушатели клавиатуры у обеих на window
 * (стрелки — свои, Escape — у Mantine Drawer). Без стека ↓ двигал обе формы, а
 * Escape закрывал обе: родитель коммитил строку и размонтировал вложенную форму
 * вместе с её черновиком.
 */
const openRowForms: object[] = [];
const rowFormStackListeners = new Set<() => void>();
const subscribeRowFormStack = (listener: () => void) => {
    rowFormStackListeners.add(listener);
    return () => {
        rowFormStackListeners.delete(listener);
    };
};
const useIsTopmostRowForm = () => {
    const [token] = useState(() => ({}));
    useLayoutEffect(() => {
        openRowForms.push(token);
        rowFormStackListeners.forEach((listener) => listener());
        return () => {
            openRowForms.splice(openRowForms.indexOf(token), 1);
            rowFormStackListeners.forEach((listener) => listener());
        };
    }, [token]);
    return useSyncExternalStore(
        subscribeRowFormStack,
        () => openRowForms.at(-1) === token || !openRowForms.includes(token),
    );
};

/** Потолок ожидания exit-перехода панели (сам переход — 220 мс). */
const DRAWER_CLOSE_FALLBACK_MS = 1000;

/** Запасной текст отказа операции: раньше сюда попадал сырой String(error).*/
const OPERATION_FAILURE_TEXT = 'Не удалось выполнить операцию. Повторите попытку.';

/** Изменения полей документа из формы строки откладываются до применения строки.
 *  Ссылка стабильна: от неё зависит контекст ChangeHandlers, а новый контекст на
 *  каждом рендере пересобирает мосты вложенных таблиц и зацикливает форму. */
const deferDocumentChange = (binding: IChangeFieldBinding) => isDocumentFieldKey(binding.key);

/**
 * Ключи boolean-полей схемы. Пустому boolean контрол на маунте дописывает ноль
 * (Field.tsx), и если ноля нет в значениях по умолчанию, форма строки становится
 * изменённой сама, без участия пользователя: каждый переход по строкам тогда
 * гонит полный пайплайн применения (запись строки, каскады, валидация всей
 * таблицы) вместо простой смены строки. Ноль подставляем сразу в defaultValues —
 * эффект контрола видит значение на месте и ничего не пишет.
 */
const collectBooleanKeys = (rows: IRow[] | undefined, acc: Set<string> = new Set<string>()): Set<string> => {
    if (!rows) return acc;
    for (const row of rows) {
        // схема генерируется по col.order — массив строк разреженный
        if (!row) continue;
        for (const column of row.columns ?? []) {
            for (const field of column.fields ?? []) {
                if (field?.valueType === ValueType.Boolean && field.key) acc.add(field.key);
            }
            collectBooleanKeys(column.rows, acc);
            for (const block of column.blocks ?? []) collectBooleanKeys(block?.rows?.row, acc);
            for (const modal of column.editInModals ?? []) collectBooleanKeys(modal?.rows?.row, acc);
        }
    }
    return acc;
};

/**
 * Значение поля формы строки совпадает с тем, что уже лежит в строке.
 * Сравнение нестрогое по типу осознанно: контрол отдаёт строку («5»), а в строке
 * лежит число — это одно и то же значение, а не правка. Объекты и массивы всегда
 * считаем изменившимися: дешёвого верного сравнения для них нет, а пропустить
 * реальное применение дороже, чем лишний раз его выполнить.
 */
const isSameRowValue = (rowValue: unknown, formValue: unknown): boolean => {
    if (rowValue !== null && typeof rowValue === 'object') return false;
    if (formValue !== null && typeof formValue === 'object') return false;
    // объекты отсеяны выше — здесь остаются скаляры и пустые значения
    const norm = (value: unknown) =>
        value === undefined || value === null
            ? ''
            : typeof value === 'boolean'
              ? String(+value)
              : String(value as string | number);
    return norm(rowValue) === norm(formValue);
};

/** Готовая к отрисовке форма одной строки: схема, карта полей и номер
 *  монтирования. Три величины меняются строго вместе — держим их одним
 *  состоянием, иначе рендер между их обновлениями получал бы карту полей от
 *  одной строки и схему от другой. */
interface IRowFormModel {
    scheme: IRows;
    fields: Record<string, IFieldElem>;
    instance: number;
    rowFields: Record<string, IFieldElem>;
    tableDataPath?: string;
}

export interface TableFormDeleteActionState {
    /** Кнопка существует для текущей строки. */
    visible: boolean;
    /** Строку разрешено удалить вычисленными правилами таблицы и строки. */
    enabled: boolean;
    /** Причина недоступности — тот же текст, что используется в строке таблицы. */
    disabledReason?: string;
}

export interface TableFormProps {
    table: IDocumentTable;
    tableDataPath?: string;
    parentAliases?: Record<string, IFieldElem>;
    /** Карта полей документа: признаки pending/loadFailed таблиц-линков есть только
     *  в ней, значения из getParentFields их не несут. */
    parentFieldMap?: Record<string, IFieldElem>;
    onClose: () => void;
    onSubmit?: (data: IField[], rowKey: unknown) => void | Promise<void>;
    getParentFields: () => IField[];
    title: string;
    isEdit: boolean;
    children?: ReactNode | ((row: Record<string, unknown>) => ReactNode);
    docId?: string;
    submitButtonCaption?: string;
    /** СТАРТОВАЯ строка сеанса: дальше по строкам ходит сама форма, и проп её
     *  не ведёт — вызывающему достаточно решить, с какой строки открыть. */
    rowIndex: number;
    rowArray: Array<Record<string, unknown>>;
    disablePagination?: boolean;
    /**
     * Режим «Значение для колонки»: значение применяется ко всем выделенным строкам.
     * Поля отбираются по схеме формы, а не по dirty-состоянию (см. persistFormData).
     */
    multiEdit?: boolean;
    /** Число уже применённых в основной RHF-черновик операций текущего сеанса. */
    sessionChangeCount?: number;
    /** Отменить все операции с момента открытия drawer. */
    onCancelSession?: () => void | Promise<void>;
    /** Создать строку и вернуть её ключ `|NUM`. */
    onAddRow?: () => Promise<unknown>;
    /** Подпись собственного способа добавления из конфигурации таблицы. */
    addRowButtonName?: string;
    /** Открыть выбор строк из справочника (набор `key`) и вернуть ключ добавленной строки. */
    onAddFormDataRows?: (key: string) => Promise<unknown>;
    /** Доступные наборы добавления из справочника — по пункту меню на каждый. */
    addFormDataRowsOptions?: { key: string; label: string }[];
    /** Удалить строку и вернуть ключ строки, которую следует открыть следующей. */
    onDeleteRow?: (rowKey: unknown) => Promise<unknown>;
    /** Вернуть последнюю удалённую строку. */
    onUndoDelete?: () => Promise<unknown>;
    canAddRow?: boolean;
    canDeleteRow?: (row: Record<string, unknown>) => boolean;
    /** Полное вычисленное состояние удаления: видимость, доступность и причина блокировки. */
    getDeleteRowActionState?: (row: Record<string, unknown>) => TableFormDeleteActionState;
    /**
     * Оболочка формы строки. По умолчанию — боковая панель справа: навигация по
     * строкам в отдельной панели под заголовком. Диалог по центру остаётся для мест,
     * где панель не подходит: форма «Значение для колонки» и вызовы из легаси-ветки.
     * @default 'drawer'
     */
    shell?: 'modal' | 'drawer';
}

const TableForm: React.FC<TableFormProps> = ({
    table,
    tableDataPath,
    parentAliases,
    parentFieldMap,
    onClose,
    onSubmit,
    getParentFields,
    title,
    isEdit,
    children,
    docId,
    submitButtonCaption,
    rowIndex,
    rowArray,
    disablePagination,
    multiEdit,
    sessionChangeCount = 0,
    onCancelSession,
    onAddRow,
    addRowButtonName,
    onAddFormDataRows,
    addFormDataRowsOptions,
    onDeleteRow,
    onUndoDelete,
    canAddRow = false,
    canDeleteRow,
    getDeleteRowActionState,
    shell = 'drawer',
}: TableFormProps) => {
    const username = useTypedSelector((state) => state.auth.user?.username ?? '');
    const parentChangeHandlers = useChangeHandlersOptional();
    const { settings: debugSettings } = useDebugPanel();
    const drawer = useTableFormDrawerWidth(username);
    const methods = useForm<ITableForm>({
        mode: 'onBlur', // "onChange"
    });
    const [rowForm, setRowForm] = useState<IRowFormModel>();
    const [operations] = useState(createRowFormOperations);
    const isTopmost = useIsTopmostRowForm();
    useLayoutEffect(() => registerFormOperationTracker(methods, operations), [methods, operations]);
    const fieldsName: number[] = [];
    const getParentFieldsRef = useLatest(getParentFields);
    const parentFieldMapRef = useLatest(parentFieldMap);

    const initialRowKey = rowArray[rowIndex]?.['|NUM'];
    const [currentRowKey, setCurrentRowKey] = useState<unknown>(initialRowKey);
    const currentRowIndex = useMemo(
        () => rowArray.findIndex((row: Record<string, unknown>) => row['|NUM'] === currentRowKey),
        [currentRowKey, rowArray],
    );
    const currentRow = currentRowIndex >= 0 ? rowArray[currentRowIndex] : undefined;
    const currentRowRef = useRef(currentRow);
    currentRowRef.current = currentRow;
    const [processing, setProcessing] = useState(false);
    const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
    const [operationError, setOperationError] = useState('');
    const deferredDocumentChangesRef = useRef(new Map<string, { binding: IChangeFieldBinding; value: unknown }>());

    /**
     * Отказ операции над строкой. Полоса статуса однострочная — в неё код обращения
     * дописывается в текст; модалка показывает его отдельной строкой с кнопкой копирования
     * и сохраняет все строки сообщения бэка.
     */
    const reportOperationFailure = useCallback(
        (error: unknown) => {
            const lines = Array.isArray(error)
                ? (error as string[]).filter(Boolean)
                : [errorText(error, OPERATION_FAILURE_TEXT)];

            // ' ' — полоса статуса однострочная (flex, align-items: center), перевод строки её ломает.
            const recovery = operations.hasFailed ? 'Отмените изменения и откройте форму заново.' : '';
            if (recovery) lines.push(recovery);
            setOperationError(
                [errorTextWithTraceId(error, OPERATION_FAILURE_TEXT, ' '), recovery].filter(Boolean).join(' '),
            );
            sendModalMsg({ message: lines, traceId: errorTraceId(error), type: 'error' });
        },
        [operations],
    );
    const [drawerOpened, setDrawerOpened] = useState(false);
    const [addMenuOpen, setAddMenuOpen] = useState(false);
    const drawerClosePendingRef = useRef(false);
    const closeFallbackRef = useRef<number | undefined>(undefined);
    /**
     * Эпоха формы строки: инкремент = «перечитать строку заново». Меняется на
     * смене строки и после успешного применения (каскад дописывает calc-колонки
     * уже после submit). Идентичность объекта строки в зависимостях не годится:
     * строку пересоздаёт любой каскад по документу, и reset посреди ввода стирал
     * бы то, что пользователь печатает прямо сейчас.
     */
    const [formEpoch, setFormEpoch] = useState(0);

    // Drawer управляемый: при закрытии сначала opened=false проигрывает штатный
    // slide-out, и только onExitTransitionEnd размонтирует TableForm в родителе.
    useEffect(() => {
        if (shell !== 'drawer') return;
        const frame = requestAnimationFrame(() => {
            if (!drawerClosePendingRef.current) setDrawerOpened(true);
        });
        return () => cancelAnimationFrame(frame);
    }, [shell]);

    const [idForm, setIdForm] = useState(() => {
        return uuidv4();
    });

    const generatedScheme = useMemo(() => (table.customForm ? undefined : generateTableFormScheme(table)), [table]);

    // Схема customForm приезжает с сервера; кэш на сессию, повторное открытие
    // панели не уходит в сеть и не мигает преloader'ом FormBuilder.
    const [customSchemeDefinition, setCustomSchemeDefinition] = useState<IForms | undefined>(() =>
        table.customForm ? customFormSchemeCache.get(table.customForm) : undefined,
    );
    const customScheme = customSchemeDefinition?.view;
    const [schemeFailed, setSchemeFailed] = useState(false);
    useEffect(() => {
        const customForm = table.customForm;
        if (!customForm) return;
        const cached = customFormSchemeCache.get(customForm);
        if (cached) {
            setCustomSchemeDefinition(cached);
            setSchemeFailed(false);
            return;
        }
        let cancelled = false;
        setSchemeFailed(false);
        void DocumentService.getTableScheme(customForm)
            .then((data) => {
                customFormSchemeCache.set(customForm, data.data);
                if (!cancelled) setCustomSchemeDefinition(data.data);
            })
            .catch((error: unknown) => {
                // без ветки отказа форма строки вечно висела на преloader'е
                console.error('TableForm: не удалось загрузить схему формы строки', error);
                if (!cancelled) setSchemeFailed(true);
            });
        return () => {
            cancelled = true;
        };
    }, [table.customForm]);
    const scheme = useMemo(
        () => applyTableDisplayMode(table.customForm ? customScheme : generatedScheme, table),
        [customScheme, generatedScheme, table],
    );
    const rowFormChangeActions = useMemo(
        () =>
            table.customForm
                ? customSchemeDefinition?.changeActions
                : Array.from(parentChangeHandlers?.runtime.actions.values() ?? []),
        [customSchemeDefinition?.changeActions, parentChangeHandlers?.runtime, table.customForm],
    );
    const changeRuntime = useChangeRuntime(rowFormChangeActions);
    const changeActions = useMemo(() => Array.from(changeRuntime.actions.values()), [changeRuntime]);
    const enqueueDeferredDocumentChange = useCallback((binding: IChangeFieldBinding, value: unknown) => {
        deferredDocumentChangesRef.current.set(binding.key, { binding, value });
    }, []);
    const flushDeferredDocumentChanges = useCallback(async () => {
        const pending = [...deferredDocumentChangesRef.current.values()];
        for (const change of pending) {
            const savedField = getParentFieldsRef.current().find((field) => field.name === change.binding.key);
            const savedValue = savedField ? savedField.value : change.value;
            // Строку формы не передаём: обработчик поля документа с ней дописал бы её в свою
            // подтаблицу (addRow) или применил бы правило к одной строке.
            // Отказ обработчика (цель пробрасывает ошибку) оставляет изменение в очереди:
            // применение строки отказывает, повторное применит его снова.
            await parentChangeHandlers?.runSavedChanges(
                { ...change.binding, scopeId: undefined, tableKey: undefined, rowId: undefined },
                savedValue,
            );
            if (deferredDocumentChangesRef.current.get(change.binding.key) === change) {
                deferredDocumentChangesRef.current.delete(change.binding.key);
            }
        }
    }, [getParentFieldsRef, parentChangeHandlers]);
    const booleanKeys = useMemo(() => collectBooleanKeys(scheme?.rows.row), [scheme]);

    /** Последняя применённая переинициализация: строка появляется в `rowArray`
     *  не обязательно тем же рендером, что меняется ключ (родитель узнаёт о
     *  новой строке из своей подписки на форму), поэтому эффект слушает и сам
     *  объект строки — но переинициализирует только на смене ключа/эпохи/схемы. */
    const initedRef = useRef<{ key: unknown; epoch: number; scheme: IRows } | null>(null);

    // layout-эффект: значения строки применяются до отрисовки кадра — иначе
    // между сменой строки и reset пользователь увидел бы чужие данные.
    useLayoutEffect(() => {
        const row = currentRow;
        if (!scheme || !row) return;
        const inited = initedRef.current;
        if (inited && inited.key === currentRowKey && inited.epoch === formEpoch) {
            if (inited.scheme === scheme) return;
            // Родитель может передать новый объект таблицы с той же схемой (новая
            // ссылка после каскада): reset тут стёр бы неприменённый ввод.
            if (isEqual(inited.scheme, scheme)) {
                inited.scheme = scheme;
                return;
            }
        }
        initedRef.current = { key: currentRowKey, epoch: formEpoch, scheme };
        let keys = getFieldKeysByScheme(scheme.rows.row, false, changeActions);
        if (!table.customForm) {
            keys.push(...collectTableColumns(table).map(({ column }) => column.key));
            keys.push(...(table.tables ?? []).map((child) => child.key));
        }
        const keysColl = Object.keys(row);
        keys = keys.concat(keysColl);
        const uniqueKeys = keys.filter((v, i, a) => v !== undefined && a.indexOf(v) === i);
        const formFields: IField[] = [...getParentFieldsRef.current()];
        const rowKeys = new Set<string>();
        for (let index = 0; index < uniqueKeys.length; index++) {
            const key = uniqueKeys[index];
            if (
                key != undefined &&
                !key.startsWith('|Parent|') &&
                ((!table.customForm && !key.includes('|Document') && !key.startsWith('#links|')) ||
                    formFields.findIndex((x) => {
                        return x.name === key;
                    }) === -1)
            ) {
                rowKeys.add(key);
                const value = row[key] as IField['value'];
                formFields.push({
                    name: key,
                    // ноль пустому boolean подставляем здесь, а не эффектом контрола:
                    // так он попадает в defaultValues и форма остаётся неизменённой
                    value: value == null && isEdit && booleanKeys.has(key) ? 0 : value,
                });
            }
        }
        methods.reset({ id: String(row['|NUM']), fields: formFields });
        // variables живут только в карте полей карточки (index -1, не в RHF): без них
        // формулы и callAction формы строки не видят глобальных переменных
        const fieldMap: Record<string, IFieldElem> = {
            ...parentAliases,
            ...(parentFieldMapRef.current?.variables
                ? { variables: liveVariablesField(() => parentFieldMapRef.current?.variables) }
                : {}),
            ...convertListToMap(formFields),
        };
        // Без признаков загрузки таблица-линк в форме строки выглядела бы пустой и
        // редактируемой, а её каскады считали бы агрегаты по заглушке.
        for (const [key, source] of Object.entries(parentFieldMapRef.current ?? {})) {
            if ((source.pending || source.loadFailed) && fieldMap[key]) {
                fieldMap[key] = { ...fieldMap[key], pending: source.pending, loadFailed: source.loadFailed };
            }
        }
        // Номер монтирования меняется здесь, УЖЕ ПОСЛЕ reset: колонке без значения
        // достаётся undefined, а такой input React считает неуправляемым и оставляет
        // в DOM текст прошлой строки (новая строка показывала чужие данные). Свежее
        // поддерево монтируется на уже применённых значениях. Заодно чистится
        // внутреннее состояние контролов — подпись справочника, маска, календарь.
        setRowForm((prev) => ({
            scheme,
            fields: fieldMap,
            rowFields: Object.fromEntries([...rowKeys].map((key) => [key, fieldMap[key]])),
            tableDataPath,
            instance: (prev?.instance ?? 0) + 1,
        }));
    }, [
        currentRow,
        scheme,
        changeActions,
        currentRowKey,
        formEpoch,
        methods,
        getParentFieldsRef,
        parentFieldMapRef,
        booleanKeys,
        isEdit,
        table,
        parentAliases,
        tableDataPath,
    ]);

    useLayoutEffect(() => {
        if (table.customForm || !rowForm?.tableDataPath) return;
        return connectRowFormDraft(methods, rowForm.tableDataPath, currentRowKey, rowForm.rowFields);
    }, [methods, table.customForm, currentRowKey, rowForm]);

    const dirtyValues = useCallback(
        (dirtyFields: unknown, allValues: ITableForm): IField[] => {
            return Object.entries(dirtyFields ?? {})
                .filter(([, dirty]) => !!dirty)
                .map(([key]) => {
                    const field = allValues.fields[+key];
                    const alias = Object.entries(rowForm?.fields ?? {}).find(
                        ([name, target]) => name.startsWith('|Parent|') && target.index === +key,
                    );
                    return field && alias ? { ...field, name: alias[0] } : field;
                })
                .filter((field): field is IField => !!field);
        },
        [rowForm],
    );

    /** Валидирует локальную форму строки и применяет её в основной RHF-черновик.
     *  Promise завершается только после внешнего pipeline changes/формул. */
    const persistFormData = useCallback(
        async (data: ITableForm): Promise<boolean> => {
            const errors: string[] = [];
            if (!table.customForm && rowForm && table.tables?.length) {
                const row = Object.fromEntries(
                    Object.entries(rowForm.rowFields).map(([key, field]) => [key, data.fields[field.index]?.value]),
                );
                const bridge = createTableFormBridge({
                    fields: rowForm.fields,
                    formMethods: methods,
                    isEdit,
                    isNew: false,
                    setError: (messages) => errors.push(...(messages ?? [])),
                });
                for (const child of table.tables) {
                    const result = await validateDocumentTable(
                        child,
                        row[child.key],
                        (rule, childRow, parentRow) =>
                            bridge.evalTableFormulaValue(
                                rule,
                                childRow as Record<string, unknown>,
                                parentRow as Record<string, unknown>,
                            ),
                        debugSettings,
                        row,
                    );
                    if (!result.valid) {
                        let issue = result.issue;
                        let definition = child;
                        while (issue.parent) {
                            definition =
                                definition.tables?.find((nested) => nested.key === issue.columnKey) ?? definition;
                            issue = issue.parent;
                        }
                        const column = collectTableColumns(definition).find(
                            ({ column }) => column.key === issue.columnKey,
                        )?.column;
                        errors.push(
                            `Проверьте поле «${column?.name || issue.columnKey}»: ${definition.name || 'вложенная таблица'}, строка ${issue.rowIndex + 1}.`,
                        );
                        break;
                    }
                }
            }
            const validators = rowForm?.scheme.validators?.validator;
            if (rowForm && validators) {
                const validatorManager = new ValidatorManager(rowForm.fields, methods);
                await validatorManager.validateAllAsync(validators, errors);
            }
            if (errors.length > 0) {
                setOperationError(errors.join(' '));
                sendModalMsg({ message: errors, type: 'error' });
                return false;
            }

            let submitData: IField[];
            if (multiEdit && rowForm) {
                // EUPDEV-12395: значение колонки применяется независимо от dirtyFields.
                submitData = getFormDataByScheme(rowForm.scheme.rows.row, data.fields, false, changeActions).fields;
            } else {
                submitData = dirtyValues(methods.formState.dirtyFields.fields, data);
            }

            // Строка фактически не изменилась: контрол мог пометить поле изменённым,
            // записав то же самое значение (число вместо его строкового вида, пустую
            // строку вместо отсутствующего значения). Применение такой «правки» стоит
            // полного пайплайна — запись строки, каскады, валидация всей таблицы, —
            // поэтому его пропускаем. Разбор полей повторяет applyEditFormFieldsToRowData:
            // ровно те поля, которые применение действительно куда-то записывает.
            const row = currentRowRef.current;
            const parentFields = getParentFieldsRef.current();
            const parentValues = new Map(parentFields.map((field) => [field.name, field.value]));
            if (!table.customForm && !multiEdit && rowForm) {
                const defaults = methods.formState.defaultValues?.fields ?? [];
                submitData = submitData.map((field) => {
                    const ownField = rowForm.rowFields[field.name];
                    const alias = field.name.startsWith('|Parent|') ? rowForm.fields[field.name] : undefined;
                    const mappedField = ownField ?? alias;
                    const before = mappedField
                        ? defaults[mappedField.index]?.value
                        : defaults.find((item) => item?.name === field.name)?.value;
                    const current = ownField
                        ? row?.[field.name]
                        : alias
                          ? parentFields[alias.index]?.value
                          : parentValues.get(field.name);
                    return { ...field, value: mergeRowFormValue(before, field.value, current) as IField['value'] };
                });
            }
            const changesSomething = (field: IField): boolean => {
                const alias = field.name.startsWith('|Parent|') ? rowForm?.fields[field.name] : undefined;
                if (alias) return !isSameRowValue(parentFields[alias.index]?.value, field.value);
                if (!table.customForm && rowForm?.rowFields[field.name])
                    return !isSameRowValue(row?.[field.name], field.value);
                if (!table.customForm && field.name === table.key)
                    return !isSameRowValue(parentValues.get(field.name), field.value);
                if (field.name.startsWith('#links|')) return false;
                if (field.name.includes('|Document')) {
                    return parentValues.has(field.name) && !isSameRowValue(parentValues.get(field.name), field.value);
                }
                // поле документа в строку не пишется — сравнивать не с чем
                if (parentValues.has(field.name)) return false;
                return !isSameRowValue(row?.[field.name], field.value);
            };
            if (!multiEdit && !!row && !submitData.some(changesSomething) && deferredDocumentChangesRef.current.size === 0) {
                setOperationError('');
                // Строку всё равно перечитываем: снимаются dirty-пометки, поставленные
                // контролами на пустом месте, и статус сеанса перестаёт обещать
                // несуществующие «неприменённые изменения».
                setFormEpoch((epoch) => epoch + 1);
                return true;
            }

            try {
                await onSubmit?.(submitData, currentRowKey);
                await flushDeferredDocumentChanges();
                setOperationError('');
                // Каскад применения дописывает calc-колонки и поля документа уже
                // после submit — форма строки обязана перечитать строку, иначе
                // показывает доприменённое состояние и остаётся dirty.
                setFormEpoch((epoch) => epoch + 1);
                return true;
            } catch (error) {
                reportOperationFailure(error);
                return false;
            }
        },
        [
            currentRowKey,
            dirtyValues,
            getParentFieldsRef,
            methods,
            multiEdit,
            onSubmit,
            rowForm,
            table,
            debugSettings,
            isEdit,
            flushDeferredDocumentChanges,
            changeActions,
        ],
    );

    const commitInFlight = useRef(false);
    const commitCurrent = useCallback(async (): Promise<boolean> => {
        if (!isEdit || !onSubmit) return true;
        if (commitInFlight.current) return false;
        commitInFlight.current = true;
        setProcessing(true);
        let committed = false;
        try {
            await operations.flush();
            if (!multiEdit && !methods.formState.isDirty && deferredDocumentChangesRef.current.size === 0) return true;
            if (!table.customForm && !multiEdit && rowForm) {
                const bridge = createTableFormBridge({
                    fields: rowForm.fields,
                    formMethods: methods,
                    isEdit,
                    isNew: false,
                    setError: (messages) => {
                        if (messages?.length) throw messages;
                    },
                });
                const row = Object.fromEntries(
                    Object.entries(rowForm.rowFields).map(([key, field]) => [
                        key,
                        methods.getValues(field.name as never),
                    ]),
                );
                // Пересчёт пишет в черновик только после успеха: его отказ прерывает
                // этот коммит, но повторить применение можно.
                const calculated = await operations.track(
                    () =>
                        calculateRowFormTree(table, row, (formula, value, parent) =>
                            bridge.evalTableFormulaValue(formula, value, parent),
                        ),
                    { critical: false },
                );
                for (const [key, field] of Object.entries(rowForm.rowFields)) {
                    if (!isEqual(row[key], calculated[key])) {
                        methods.setValue(field.name as never, calculated[key] as never, { shouldDirty: true });
                    }
                }
                await operations.flush();
            }
            await methods.handleSubmit(
                async (data) => {
                    committed = await persistFormData(data);
                },
                () => {
                    committed = false;
                },
            )();
            return committed;
        } catch (error) {
            reportOperationFailure(error);
            return false;
        } finally {
            commitInFlight.current = false;
            setProcessing(false);
        }
    }, [isEdit, methods, onSubmit, persistFormData, operations, reportOperationFailure, table, multiEdit, rowForm]);

    const submitCurrent = useCallback(async () => {
        await commitCurrent();
    }, [commitCurrent]);

    const selectRow = useCallback((rowKey: unknown) => {
        setCurrentRowKey(rowKey);
        setIdForm(uuidv4());
        setOperationError('');
        setAddMenuOpen(false);
    }, []);

    // ----- Боковая панель (shell='drawer'): навигация по строкам -----
    const canNavigate = shell === 'drawer' && !disablePagination && rowArray.length > 1;
    const goToRow = useCallback(
        async (index: number) => {
            if (processing || index < 0 || index >= rowArray.length || index === currentRowIndex) return;
            const targetKey = rowArray[index]?.['|NUM'];
            if (!(await commitCurrent())) return;
            selectRow(targetKey);
        },
        [commitCurrent, currentRowIndex, processing, rowArray, selectRow],
    );

    // стрелки ↑/↓ ходят по строкам, пока фокус не в поле ввода
    useEffect(() => {
        if (!canNavigate || processing || cancelConfirmOpen || !isTopmost) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
            const target = e.target as HTMLElement | null;
            if (target?.closest('input, textarea, select, [contenteditable="true"], .rf-select')) return;
            e.preventDefault();
            void goToRow(currentRowIndex + (e.key === 'ArrowDown' ? 1 : -1));
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [canNavigate, cancelConfirmOpen, currentRowIndex, goToRow, isTopmost, processing]);

    const hasSessionChanges = isEdit && (methods.formState.isDirty || sessionChangeCount > 0 || deferredDocumentChangesRef.current.size > 0);

    const finishDrawerClose = useCallback(() => {
        if (!drawerClosePendingRef.current) return;
        drawerClosePendingRef.current = false;
        window.clearTimeout(closeFallbackRef.current);
        onClose();
    }, [onClose]);

    const closeAfterTransition = useCallback(() => {
        if (shell !== 'drawer' || !drawerOpened) {
            onClose();
            return;
        }
        if (drawerClosePendingRef.current) return;
        drawerClosePendingRef.current = true;
        setDrawerOpened(false);
        // Страховка: exit-переход Mantine доигрывается по requestAnimationFrame,
        // а он не тикает в фоновой вкладке. Без неё панель, закрытая перед уходом
        // со вкладки, оставалась бы смонтированной и не открывалась заново.
        window.clearTimeout(closeFallbackRef.current);
        closeFallbackRef.current = window.setTimeout(finishDrawerClose, DRAWER_CLOSE_FALLBACK_MS);
    }, [drawerOpened, finishDrawerClose, onClose, shell]);

    useEffect(() => () => window.clearTimeout(closeFallbackRef.current), []);

    const cancelSessionAndClose = useCallback(async () => {
        setProcessing(true);
        try {
            await operations.flush().catch(() => undefined);
            deferredDocumentChangesRef.current.clear();
            await onCancelSession?.();
            closeAfterTransition();
        } catch (error) {
            reportOperationFailure(error);
        } finally {
            setProcessing(false);
        }
    }, [closeAfterTransition, onCancelSession, operations]);

    const requestClose = useCallback(
        (reason?: ModalCloseReason) => {
            if (processing) return;
            const accidental = isAccidentalClose(reason);
            // Крестик закрывает сразу, но применённые строки сеанса без вопроса не откатывает
            const appliedChanges =
                sessionChangeCount > 0 || operations.isPending || deferredDocumentChangesRef.current.size > 0;
            if (appliedChanges || (accidental && hasSessionChanges)) {
                setCancelConfirmOpen(true);
                return;
            }
            if (isEdit && !accidental) void cancelSessionAndClose();
            else closeAfterTransition();
        },
        [
            cancelSessionAndClose,
            closeAfterTransition,
            hasSessionChanges,
            isEdit,
            operations,
            processing,
            sessionChangeCount,
        ],
    );

    const finishAndClose = useCallback(async () => {
        if (processing || !(await commitCurrent())) return;
        closeAfterTransition();
    }, [closeAfterTransition, commitCurrent, processing]);

    const runAddAction = useCallback(
        async (action?: () => Promise<unknown>) => {
            if (!action || processing || !(await commitCurrent())) return;
            setProcessing(true);
            try {
                const rowKey = await action();
                if (rowKey !== undefined) selectRow(rowKey);
            } catch (error) {
                reportOperationFailure(error);
            } finally {
                setProcessing(false);
            }
        },
        [commitCurrent, processing, selectRow],
    );

    const canAddOwnRow = canAddRow && !!onAddRow;
    const addMenuItems = useMemo(
        () =>
            tableAddMenuItems(
                canAddOwnRow
                    ? {
                          value: 'row',
                          label: addRowButtonName || 'Добавить строку',
                          handler: () => void runAddAction(onAddRow),
                      }
                    : undefined,
                onAddFormDataRows
                    ? (addFormDataRowsOptions ?? []).map((option) => ({
                          value: `formData:${option.key}`,
                          label: option.label,
                          handler: () => void runAddAction(() => onAddFormDataRows(option.key)),
                      }))
                    : [],
            ),
        [addFormDataRowsOptions, addRowButtonName, canAddOwnRow, onAddFormDataRows, onAddRow, runAddAction],
    );
    const addChoices = addMenuItems.filter((item) => !item.isTitle);

    const deleteRowFromDrawer = useCallback(async () => {
        if (!onDeleteRow || processing || currentRowKey === undefined) return;
        setProcessing(true);
        try {
            const nextRowKey = await onDeleteRow(currentRowKey);
            // Неприменённые правки удалённой строки уходят вместе с ней. Следующая
            // строка перечитает форму сама, а без неё форма осталась бы dirty и
            // «Вернуть строку» применял бы их к несуществующей строке.
            if (nextRowKey == null) methods.reset();
            selectRow(nextRowKey ?? null);
        } catch (error) {
            reportOperationFailure(error);
        } finally {
            setProcessing(false);
        }
    }, [currentRowKey, methods, onDeleteRow, processing, reportOperationFailure, selectRow]);

    const undoDelete = useCallback(async () => {
        if (!onUndoDelete || processing) return;
        setProcessing(true);
        let restoredRowKey: unknown;
        try {
            restoredRowKey = await onUndoDelete();
        } catch (error) {
            reportOperationFailure(error);
            return;
        } finally {
            setProcessing(false);
        }
        // Снимок до удаления вернул и текущую строку. Неприменённые правки формы —
        // дельта к ней: применяем их поверх и только потом открываем возвращённую
        // строку. Применить раньше нельзя — возврат снимка их бы откатил.
        if (restoredRowKey !== undefined && (await commitCurrent())) selectRow(restoredRowKey);
    }, [commitCurrent, onUndoDelete, processing, reportOperationFailure, selectRow]);

    const modalFooter = (
        <>
            <div className={'modal-dlg-buttons'}>
                {isEdit && (
                    <Button
                        type="submit"
                        size="sm"
                        disabled={
                            processing || methods.formState.isSubmitting || (!multiEdit && !methods.formState.isDirty)
                        }
                        form={idForm}
                    >
                        {submitButtonCaption ? submitButtonCaption : 'Сохранить'}
                    </Button>
                )}
                <Button onClick={onClose} variant="light" size="sm" disabled={processing}>
                    {isEdit ? 'Отменить' : 'Ок'}
                </Button>
            </div>
            {!disablePagination && (
                <Pagination
                    count={rowArray.length}
                    getCurrentPage={(page) => void goToRow(page - 1)}
                    startingPage={Math.max(1, currentRowIndex + 1)}
                    hidePageInput={true}
                    className={'table-form__pagination'}
                />
            )}
        </>
    );

    const modalSize = table.tableFormModalSize ?? 'xl';

    const sessionStatus = operationError
        ? `Не удалось применить изменения: ${operationError}`
        : processing
          ? 'Применяем изменения…'
          : methods.formState.isDirty
            ? 'Есть неприменённые изменения в текущей строке'
            : sessionChangeCount > 0
              ? `Изменений в черновике карточки: ${sessionChangeCount}`
              : isEdit && currentRow
                ? 'Изменения применяются при переходе между строками'
                : isEdit
                  ? 'Можно добавить новую строку или вернуть удалённую'
                  : 'Просмотр строки таблицы';

    // меню — от двух способов добавления; один способ (своя строка или единственный набор) — сразу кнопка
    const soleChoice = addChoices.length === 1 ? addChoices[0] : undefined;
    const addActionControl = soleChoice ? (
        <Button
            type="button"
            variant="outline-thin"
            size="sm"
            startAdornment={tableAddChoiceIcon(soleChoice.value ?? '')}
            disabled={processing}
            onClick={soleChoice.handler}
        >
            {soleChoice.label}
        </Button>
    ) : addChoices.length > 1 ? (
        <Menu
            list={addMenuItems}
            opened={addMenuOpen}
            onOpenChange={setAddMenuOpen}
            position="bottom-start"
            popoverProps={{ className: TABLE_ADD_MENU_CLASS, listItemAlign: 'start' }}
        >
            <Button
                type="button"
                variant="outline-thin"
                size="sm"
                startAdornment={<MdAdd size="17" aria-hidden />}
                endAdornment={<MdKeyboardArrowDown size="17" aria-hidden />}
                disabled={processing}
            >
                {addRowButtonName || 'Добавить'}
            </Button>
        </Menu>
    ) : null;

    const fallbackCanDelete = currentRow ? (canDeleteRow?.(currentRow) ?? false) : false;
    const deleteActionState: TableFormDeleteActionState = currentRow
        ? (getDeleteRowActionState?.(currentRow) ?? {
              visible: fallbackCanDelete,
              enabled: fallbackCanDelete,
          })
        : { visible: false, enabled: false };

    const drawerHeader = (
        <div className="table-form__dw-header" data-testid="table-form-drawer-header">
            <div className="table-form__dw-head">
                <span className="table-form__dw-heading">
                    <span className="table-form__dw-title">{table.name || title}</span>
                    <span className="table-form__dw-mode">
                        <Chip variant={isEdit ? 'fill' : 'secondary'} size="xs" isButton={false}>
                            {isEdit ? 'Редактирование' : 'Только просмотр'}
                        </Chip>
                    </span>
                </span>
            </div>
            {canNavigate && (
                <TableFormNavigation
                    currentIndex={currentRowIndex}
                    count={rowArray.length}
                    disabled={processing}
                    onNavigate={(index) => void goToRow(index)}
                />
            )}
            <div className="table-form__dw-toolbar" data-testid="table-form-drawer-toolbar">
                <span className="table-form__dw-actions">
                    {isEdit && addActionControl}
                    {isEdit && currentRow && deleteActionState.visible && onDeleteRow && (
                        <Tooltip
                            openDelay={300}
                            background="black"
                            position="bottom"
                            label={
                                deleteActionState.enabled
                                    ? 'Удалить строку'
                                    : (deleteActionState.disabledReason ?? 'Удаление недоступно')
                            }
                        >
                            <Button
                                type="button"
                                variant="outline-thin"
                                tone="danger"
                                size="sm"
                                className="table-form__dw-delete"
                                startAdornment={<MdOutlineDelete size="16" aria-hidden />}
                                aria-label="Удалить строку"
                                disabled={processing || !deleteActionState.enabled}
                                onClick={() => void deleteRowFromDrawer()}
                            >
                                Удалить
                            </Button>
                        </Tooltip>
                    )}
                </span>
                <span
                    className={`table-form__dw-status${operationError ? ' table-form__dw-status--error' : ''}`}
                    role="status"
                >
                    <span className="table-form__dw-status-dot" aria-hidden />
                    <span className="table-form__dw-status-text">{sessionStatus}</span>
                    {onUndoDelete && (
                        <button type="button" onClick={() => void undoDelete()} disabled={processing}>
                            <MdUndo size="14" aria-hidden />
                            Вернуть строку
                        </button>
                    )}
                </span>
            </div>
        </div>
    );

    const drawerFooter = (
        <div className="table-form__dw-footer-actions" data-testid="table-form-drawer-footer-actions">
            <Button
                onClick={() => void finishAndClose()}
                variant={isEdit ? 'primary' : 'outline-thin'}
                size="sm"
                disabled={processing}
                loading={processing}
            >
                {isEdit ? 'Готово' : 'Закрыть'}
            </Button>
            {isEdit && (
                <Button
                    onClick={() => void cancelSessionAndClose()}
                    variant="outline-neutral"
                    size="sm"
                    disabled={processing || !hasSessionChanges}
                >
                    Отменить изменения
                </Button>
            )}
        </div>
    );

    const renderedChildren = typeof children === 'function' ? (currentRow ? children(currentRow) : null) : children;

    const form = schemeFailed ? (
        <div className="table-form__empty">
            <span>Не удалось загрузить форму строки. Обновите страницу и попробуйте ещё раз.</span>
        </div>
    ) : currentRow ? (
        <FormProviderControl
            id={idForm}
            formMethods={methods}
            className="table-form"
            onSubmit={submitCurrent}
            onInvalid={submitCurrent}
            changeRuntime={changeRuntime}
            deferChange={deferDocumentChange}
            onDeferredChange={enqueueDeferredDocumentChange}
        >
            <HookFormProvider controller={Controller}>
                <div className={'table-form__wrapper'} inert={processing} aria-busy={processing}>
                    {renderedChildren}
                    <div className="table-form__content">
                        <NestedFormBuilder
                            // ключ монтирования формы строки — см. layout-эффект применения строки
                            key={rowForm?.instance ?? 0}
                            fields={rowForm?.fields ?? EMPTY_FIELDS}
                            docId={docId}
                            setError={(errors?: string[]) => {
                                if (errors) {
                                    sendModalMsg({
                                        message: errors,
                                        type: 'error',
                                    });
                                }
                            }}
                            rows={rowForm?.scheme}
                            isEdit={isEdit}
                            isNew={false}
                            formMethods={methods}
                            includedFields={fieldsName}
                            uniqueKey={docId ? docId : '-1'}
                        />
                        {!table.customForm && !!table.tables?.length && rowForm && (
                            <TableFormSubtables
                                key={`subtables-${rowForm.instance}`}
                                table={table}
                                fields={rowForm.fields}
                                rowFields={rowForm.rowFields}
                                methods={methods}
                                isEdit={isEdit}
                                docId={docId}
                                setError={(errors) => {
                                    if (errors?.length) sendModalMsg({ message: errors, type: 'error' });
                                }}
                            />
                        )}
                    </div>
                </div>
            </HookFormProvider>
        </FormProviderControl>
    ) : (
        <div className="table-form__empty">
            <span>В таблице пока нет строк</span>
        </div>
    );

    return (
        <RowFormOperationsContext value={operations}>
            <TableFormProvider editInForm={isEdit} viewInForm={!isEdit}>
                {shell === 'drawer' ? (
                    <Drawer
                        onClose={requestClose}
                        opened={drawerOpened}
                        onExitTransitionEnd={finishDrawerClose}
                        header={drawerHeader}
                        footer={drawerFooter}
                        className="table-form__drawer"
                        width={drawer.width}
                        withOverlay
                        closeOnEscape={isTopmost && !cancelConfirmOpen && !processing}
                        closeOnClickOutside={!cancelConfirmOpen && !processing}
                    >
                        <button
                            type="button"
                            className={clsx('table-form__drawer-resize', drawer.resizing && 'is-resizing')}
                            aria-label="Изменить ширину панели"
                            title="Потяните, чтобы изменить ширину. Двойной клик — ширина по умолчанию"
                            onPointerDown={(event) => {
                                event.preventDefault();
                                drawer.startResize(event.clientX);
                            }}
                            onDoubleClick={drawer.resetWidth}
                            onKeyDown={(event) => {
                                if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
                                event.preventDefault();
                                drawer.resizeBy(event.key === 'ArrowLeft' ? 32 : -32);
                            }}
                        ></button>
                        {form}
                        {cancelConfirmOpen && (
                            <MessageDialog
                                header="Отменить изменения?"
                                message="Все изменения, внесённые в таблицу после открытия панели, будут отменены."
                                okButtonText="Отменить изменения"
                                cancelButtonText="Продолжить редактирование"
                                // отказ отмены cancelSessionAndClose показывает сам — окно закрывается после попытки
                                onConfirm={async () => {
                                    await cancelSessionAndClose();
                                    setCancelConfirmOpen(false);
                                }}
                                onCancel={() => setCancelConfirmOpen(false)}
                                loading={processing}
                            />
                        )}
                    </Drawer>
                ) : (
                    <Modal
                        size={modalSize}
                        onClose={() => {
                            if (!processing) onClose();
                        }}
                        hasUnsavedInput={() => hasSessionChanges}
                        closeDisabled={processing}
                        header={title}
                        footer={modalFooter}
                    >
                        {form}
                    </Modal>
                )}
            </TableFormProvider>
        </RowFormOperationsContext>
    );
};

export default TableForm;
